import { MacroRow, PulseEarningsRow, PulseItem, ShortMover } from "@/models/pulse";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import remarkGfm from "remark-gfm";
import { MIContent } from "@/models/market-intelligence";
import LockedArticle from "@/components/market-intelligence/LockedArticle";
import { contentTypeLabel } from "@/components/market-intelligence/utils";
import styles from "./MarketsPulse.module.scss";
import { DetailContent, DetailFact } from "./DetailDialog";
import { CATEGORY_META, clockTime, itemHref, money, pct, signed, stockHref, timeAgo } from "./utils";

export const CategoryTag = ({ category }: { category: PulseItem["category"] }) => <span className={`${styles.tag} ${styles[`tag-${category}`]}`}>{CATEGORY_META[category]?.tag}</span>;

const toneOf = (direction: PulseItem["direction"]) => (direction === "up" ? "up" : direction === "down" ? "down" : undefined);

/** The drill-in for one pulse item: what it is, why it ranks, where to go next. */
export const itemDetail = (item: PulseItem): DetailContent => {
    const facts: DetailFact[] = [];
    if (item.valueText) facts.push({ label: "Reading", value: item.valueText, tone: toneOf(item.direction) });
    facts.push({ label: "Interest score", value: `${Math.round(item.score)} / 100` });
    if (item.timestamp) facts.push({ label: "Seen", value: `${clockTime(item.timestamp)} · ${timeAgo(item.timestamp)} ago` });
    const extra = item.extra || {};
    if (item.category === "shorts") {
        if (extra.costToBorrow !== undefined && extra.costToBorrow !== null) facts.push({ label: "Cost to borrow", value: pct(extra.costToBorrow, 2) });
        if (extra.daysToCover !== undefined && extra.daysToCover !== null) facts.push({ label: "Days to cover", value: extra.daysToCover.toFixed(1) });
        if (extra.siChangeWeekPp !== undefined && extra.siChangeWeekPp !== null) facts.push({ label: "Change, 1 week", value: signed(extra.siChangeWeekPp, 1, "pp"), tone: extra.siChangeWeekPp > 0 ? "down" : "up" });
    }
    if (item.category === "insiders") {
        if (extra.insiderRole) facts.push({ label: "Role", value: extra.insiderRole });
        if (extra.clusterCount > 1) facts.push({ label: "Insiders buying", value: extra.clusterCount });
        if (extra.buyerCount) facts.push({ label: "Buyers / sellers", value: `${extra.buyerCount} / ${extra.sellerCount ?? 0}` });
    }
    if (item.category === "gov") {
        if (extra.chamber) facts.push({ label: "Chamber", value: extra.chamber });
        if (extra.party) facts.push({ label: "Party", value: extra.party });
        if (extra.lagDays !== null && extra.lagDays !== undefined) facts.push({ label: "Disclosure lag", value: `${extra.lagDays} days` });
    }
    if (item.category === "options") {
        facts.push({ label: "Call premium", value: money(extra.callPremium), tone: "up" });
        facts.push({ label: "Put premium", value: money(extra.putPremium), tone: "down" });
    }
    if (item.category === "macro" && extra.sigma !== undefined) facts.push({ label: "Move size", value: `${Math.abs(extra.sigma).toFixed(1)}σ` });

    const links: DetailContent["links"] = [];
    const stock = stockHref(item.exchangeSymbol, item.ticker);
    if (stock) links.push({ label: `${item.ticker} stock page`, href: stock });
    const section = item.link || CATEGORY_META[item.category]?.href;
    if (section) links.push({ label: `Open ${CATEGORY_META[item.category]?.label || "section"}`, href: section, primary: true });

    return {
        title: item.headline,
        tag: <CategoryTag category={item.category} />,
        facts,
        body: (
            <>
                {item.detail && <p style={{ margin: 0 }}>{item.detail}</p>}
                {item.company && item.ticker && (
                    <p style={{ margin: 0 }} className={styles.faint}>
                        {item.company} · {item.ticker}
                        {item.exchangeSymbol ? ` · ${item.exchangeSymbol}` : ""}
                    </p>
                )}
                {item.interest !== undefined && (
                    <p style={{ margin: 0, fontSize: 12 }} className={styles.faint}>
                        Ranked by how unusual the reading is for its own dataset, fading with age. Current weight {item.interest.toFixed(0)}.
                    </p>
                )}
            </>
        ),
        links,
    };
};

const weekTone = (change: number | null) => (change === null ? undefined : change > 0 ? "down" : "up");

export const shortMoverDetail = (row: ShortMover): DetailContent => ({
    title: `${row.ticker} · ${row.companyName || ""} short interest`,
    tag: <CategoryTag category="shorts" />,
    facts: [
        { label: "SI % free float", value: pct(row.siPercentFf), tone: "down" },
        ...(row.siChangePp === null ? [] : [{ label: "Change today", value: signed(row.siChangePp, 2, "pp"), tone: weekTone(row.siChangePp) } as DetailFact]),
        { label: "Change, 1 week", value: row.siChangeWeekPp === null ? "—" : signed(row.siChangeWeekPp, 1, "pp"), tone: weekTone(row.siChangeWeekPp) },
        ...(row.costToBorrow === null ? [] : [{ label: "Cost to borrow", value: pct(row.costToBorrow, 2) }]),
        { label: "Days to cover", value: row.daysToCover === null ? "—" : row.daysToCover.toFixed(1) },
        { label: "Market cap", value: money(row.marketCapUsd) },
    ],
    body: (
        <p style={{ margin: 0 }} className={styles.muted}>
            {row.siChangePp === null
                ? `Short interest as % of free float, against a week ago (${pct(row.siPercentFfWeekAgo)}).`
                : `Short interest as % of free float, against the last close (${pct(row.siPercentFfPrev)})${row.siPercentFfWeekAgo === null ? "" : ` and a week ago (${pct(row.siPercentFfWeekAgo)})`}.`}
        </p>
    ),
    links: [...(stockHref(row.exchangeSymbol, row.ticker) ? [{ label: "Stock short interest", href: `${stockHref(row.exchangeSymbol, row.ticker)}/short-interest` }] : []), { label: "Short interest screener", href: "/short-interest", primary: true }],
});

export const macroDetail = (row: MacroRow, threshold: number): DetailContent => ({
    title: row.name,
    tag: <CategoryTag category="macro" />,
    facts: [
        { label: "Last", value: row.kind === "yield" ? `${row.last.toFixed(2)}%` : row.last.toLocaleString(undefined, { maximumFractionDigits: 4 }) },
        {
            label: "Last session",
            value: row.kind === "yield" ? signed(row.changeBp, 0, "bp") : signed(row.changePct, 2, "%"),
            tone: (row.kind === "yield" ? row.changeBp : row.changePct) >= 0 ? "up" : "down",
        },
        { label: "Normal daily move", value: row.kind === "yield" ? `${(row.dailyVolBp ?? 0).toFixed(1)}bp` : `${(row.dailyVolPct ?? 0).toFixed(2)}%` },
        { label: "Move size", value: row.sigma === null ? "—" : `${Math.abs(row.sigma).toFixed(1)}σ` },
    ],
    body: (
        <p style={{ margin: 0 }} className={styles.muted}>
            Shown on the front page when the move is at least {threshold}σ of its normal daily range
            {row.kind === "yield" ? " (estimated from the week and month changes)" : " (60-session realised volatility)"}. It drops off again when the move fades.
        </p>
    ),
    links: [{ label: row.kind === "fx" ? "Currencies" : row.kind === "commodity" ? "Commodities" : "Bond Yields", href: row.kind === "fx" ? "/currencies" : row.kind === "commodity" ? "/commodities" : "/yields", primary: true }],
});

const TIMING_LABEL = { before_open: "Before open", after_close: "After close", during_market: "During market", unknown: "Time TBC" };

export const earningsWhen = (row: PulseEarningsRow) => {
    const day = new Date(`${row.reportDay}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    return `${day} · ${TIMING_LABEL[row.timing]}`;
};

export const earningsDetail = (row: PulseEarningsRow): DetailContent => ({
    title: `${row.ticker} · ${row.company} earnings`,
    tag: <span className={`${styles.tag} ${styles["tag-news"]}`}>EARNINGS</span>,
    facts: [
        { label: "Reports", value: earningsWhen(row) + (row.estimatedDate ? " (est.)" : "") },
        { label: "Implied move", value: row.impliedMovePct === null ? "—" : `±${row.impliedMovePct.toFixed(1)}%` },
        { label: "SI % free float", value: pct(row.siPercentFf) },
        { label: "Cost to borrow", value: pct(row.costToBorrow, 2) },
        { label: "Market cap", value: money(row.marketCap) },
    ],
    body: (
        <p style={{ margin: 0 }} className={styles.muted}>
            Implied move is the at-the-money straddle of the first expiry after the report, as a share of the last close.
        </p>
    ),
    links: [...(stockHref(row.exchangeSymbol, row.ticker) ? [{ label: `${row.ticker} stock page`, href: stockHref(row.exchangeSymbol, row.ticker) }] : []), { label: "Earnings calendar", href: "/earnings", primary: true }],
});

export const stripMarkdown = (text: string) =>
    (text || "")
        .replace(/[#*_>`]/g, "")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/\s+/g, " ")
        .trim();

/** Commentary and Market Intelligence content, opened by a click or by its link (?show=article&id=). */
export const articleDetail = (content: MIContent): DetailContent => {
    const commentary = (content.contentType as string) === "market_commentary";
    const kind = commentary ? "COMMENTARY" : contentTypeLabel(content.contentType).replace(/_/g, " ").toUpperCase();
    const stock = stockHref(content.exchange, content.ticker);
    return {
        title: content.headline,
        tag: <span className={`${styles.tag} ${styles["tag-signals"]}`}>{kind}</span>,
        // Locked content arrives as a teaser; the gate is the same one the article page shows. The commentary
        // lead reads as short lines; digests, reports and previews keep their headings, lists and tables.
        body: content.locked ? (
            <LockedArticle content={content} />
        ) : commentary ? (
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{stripMarkdown(content.body).replace(/\. /g, ".\n")}</div>
        ) : (
            <div style={{ lineHeight: 1.55 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.body || ""}</ReactMarkdown>
            </div>
        ),
        links: [...(stock ? [{ label: `${content.ticker} stock page`, href: stock }] : []), { label: "Open in Market Intelligence", href: `/market-intelligence/articles/${content.id}`, primary: true }],
    };
};

export { itemHref };
